<template>
  <div
    class="approval-flow-slot pl-4"
    :class="borderClass"
  >
    <div class="approval-flow-slot-inner d-flex flex-row ga-2">
      <div class="d-flex flex-column ga-1 flex-grow-1">
        <div class="d-flex align-center">
          <span class="text-body-1 text-textDarkest font-weight-medium">{{ flowSlot.name }}</span>
          <DpplTooltip
            v-if="flowSlot.description"
            tooltip-placement="top"
            class="approval-flow-slot-info-tooltip"
          >
            <template #activator="{ onMouseenter, onMouseleave }">
              <span
                class="d-inline-flex"
                @mouseenter="onMouseenter"
                @mouseleave="onMouseleave"
              >
                <v-icon
                  icon="$information-outline"
                  size="16"
                  color="iconDark"
                  class="ml-1"
                />
              </span>
            </template>
            <template #tooltip-content>
              <div class="text-body-2 text-textDark">
                {{ flowSlot.description }}
              </div>
            </template>
          </DpplTooltip>
        </div>

        <ApprovalFlowSlotField label="Status">
          <ApprovalStatusPill
            :status="flowSlot.status"
            mode="approvalSlot"
          />
        </ApprovalFlowSlotField>

        <ApprovalFlowSlotField label="Assigned">
          <span class="text-body-2 text-textDarkest font-weight-medium">
            {{ assignedNames }}
          </span>
        </ApprovalFlowSlotField>

        <template v-if="showActionedFields">
          <ApprovalFlowSlotField label="Actioned">
            <span class="text-body-2 text-textDarkest font-weight-medium">
              {{ formatUserDisplayName(flowSlot?.acted_by) || '—' }}
            </span>
          </ApprovalFlowSlotField>

          <ApprovalFlowSlotField label="Date">
            <span class="text-body-2 text-textDarkest font-weight-medium">
              {{ formattedActedAt }}
            </span>
          </ApprovalFlowSlotField>
        </template>

        <ApprovalApproverSearch
          v-if="mode === 'edit' && (flowSlot.status === 'pending' || flowSlot.status === 'staged')"
          :model-value="selectedUsers"
          :exclude-ids="excludeApproverIds"
          @update:model-value="$emit('update:selectedUsers', $event)"
        />
      </div>

      <div
        v-if="$slots.actions"
        class="slot-actions d-flex align-center"
      >
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import ApprovalFlowSlotField from '@/components/jobProfiles/editor/approvals/ApprovalFlowSlotField.vue';
import ApprovalStatusPill from '@/components/jobProfiles/editor/ApprovalStatusPill.vue';
import ApprovalApproverSearch from '@/components/jobProfiles/editor/ApprovalApproverSearch.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import { formatDisplayDate } from '@/helpers/date';
import { formatUserDisplayName } from '@/helpers/user';

const SLOT_BORDER_CLASSES = {
  pending: 'approval-flow-slot-border-pending',
  approved: 'approval-flow-slot-border-approved',
  rejected: 'approval-flow-slot-border-rejected',
};

const props = defineProps({
  flowSlot: {
    type: Object,
    required: true,
  },
  mode: {
    type: String,
    default: 'view',
    validator: (value) => ['view', 'edit'].includes(value),
  },
  selectedUsers: {
    type: Array,
    default: () => [],
  },
});

defineEmits(['update:selectedUsers']);

const borderClass = computed(() => (
  SLOT_BORDER_CLASSES[String(props.flowSlot?.status || '').toLowerCase()]
  ?? 'approval-flow-slot-border-queued'
));

const assignedNames = computed(() => {
  const approvers = props.flowSlot?.approvers ?? [];

  if (!approvers.length) return 'None';

  const names = approvers.map(formatUserDisplayName).filter(Boolean);

  return names.length ? names.join(', ') : '—';
});

const normalizedStatus = computed(() => String(props.flowSlot?.status || '').toLowerCase());

const showActionedFields = computed(() => (
  ['approved', 'rejected'].includes(normalizedStatus.value)
));

const formattedActedAt = computed(() => {
  const formatted = formatDisplayDate(props.flowSlot?.acted_at);

  return formatted === '—' ? '' : formatted;
});

const excludeApproverIds = computed(() => {
  const existingIds = props.flowSlot.approvers.map((approver) => approver.ulid);
  const addedIds = props.selectedUsers.map((selectedUser) => selectedUser.id);

  return [...new Set([...existingIds, ...addedIds])];
});
</script>

<style scoped lang="less">
.approval-flow-slot {
  border-left-width: 3px;
  border-left-style: solid;

  :deep(.approval-approver-search .label-row) {
    display: flex;
    padding-bottom: 0 !important;
  }
}

.approval-flow-slot-border-pending {
  border-left-color: @yellow-500;
}

.approval-flow-slot-border-approved {
  border-left-color: @teal-500;
}

.approval-flow-slot-border-rejected {
  border-left-color: @red-500;
}

.approval-flow-slot-border-queued {
  border-left-color: @grey-500;
}

.approval-flow-slot-info-tooltip {
  :deep(.activator) {
    display: flex;
    align-items: center;
  }
}
</style>
